import { formatString } from "@/helpers/formatting";
import { axiosClient } from "@/services/client";
import endpoints from "@/services/endpoints";

import type {
  AiAgentsOptions,
  AttributeOptions,
  CheckoutOptions,
  ContractTemplateOptions,
  DynamicFieldOptions,
  IBorrowerPlatformTemplatesOptions,
  IOptions,
  IOptionsApplication,
  IOptionsState,
  IOptionsWorkflowTemplates,
  KeyboardShortcutPayload,
} from "@/models/options";

class OptionsApiService {
  async getAttributeOptions() {
    const response = await axiosClient.get<{ data: AttributeOptions }>(
      endpoints.GET_OPTIONS.ATTRIBUTE
    );
    return response.data.data;
  }
  async getOptions(params: Record<string, string | number>) {
    const response = await axiosClient.get<{ data: IOptions }>(
      endpoints.GET_OPTIONS.ALL,
      { params }
    );
    return response.data.data;
  }
  async getDynamicFields() {
    const response = await axiosClient.get<{ data: DynamicFieldOptions }>(
      endpoints.GET_OPTIONS.DYNAMIC_FIELDS
    );
    return response.data.data;
  }
  async getApplicationsOptions() {
    const response = await axiosClient.get<{ data: IOptionsApplication }>(
      endpoints.GET_OPTIONS.APPLICATION
    );
    return response.data.data;
  }
  async getWorkflowTemplatesOptions() {
    const response = await axiosClient.get<{ data: IOptionsWorkflowTemplates }>(
      endpoints.GET_OPTIONS.WORKFLOW_TEMPLATES
    );
    return response.data.data;
  }
  async getContractTemplatesOptions() {
    const response = await axiosClient.get<{ data: ContractTemplateOptions }>(
      endpoints.GET_OPTIONS.CONTRACT_TEMPLATES
    );
    return response.data.data;
  }
  async getSingleAttributeOptions(id: string) {
    const response = await axiosClient.get<{
      data: { id: string; options: Record<string, string> };
    }>(formatString(endpoints.GET_OPTIONS.SINGLE_ATTRIBUTE_OPTIONS, id));
    return response.data.data;
  }
  async getOfferTemplatesOptions() {
    const response = await axiosClient.get<{ data: Record<string, unknown> }>(
      endpoints.GET_OPTIONS.OFFER
    );
    return response.data.data;
  }
  async getCheckoutOptions() {
    const response = await axiosClient.get<{ data: CheckoutOptions }>(
      endpoints.GET_OPTIONS.CHECKOUT
    );

    return response.data.data;
  }
  async getNaicsCodes(params: Record<string, unknown> = {}) {
    const response = await axiosClient.get<{ data: Record<string, string> }>(
      formatString(endpoints.GET_OPTIONS.NAICS_CODES),
      {
        params,
      }
    );
    return response.data.data;
  }
  async getAiAgentsOptions() {
    const response = await axiosClient.get<{ data: AiAgentsOptions }>(
      endpoints.GET_OPTIONS.AI_AGENTS
    );

    return response.data.data;
  }
  async getBorrowerPlatformTemplatesOptions() {
    const response = await axiosClient.get<{
      data: IBorrowerPlatformTemplatesOptions;
    }>(endpoints.GET_OPTIONS.BORROWER_PLATFORM_TEMPLATES);

    return response.data.data;
  }

  async getDataOrchestrationOptions() {
    const response = await axiosClient.get<{
      data: IOptionsState["dataOrchestration"];
    }>(endpoints.GET_OPTIONS.DATA_ORCHESTRATION);
    return response.data.data;
  }

  async updateKeyboardShortcuts(payload: KeyboardShortcutPayload) {
    return await axiosClient.post<{ data: [] }>(
      endpoints.KEYBOARD_SHORTCUTS.UPDATE,
      payload
    );
  }
}

const optionsApiService = new OptionsApiService();
export default optionsApiService;
